import { organizationApi } from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useFormError } from '@/hooks/use-form-errors';
import { USER_QUERY_KEY, useSession } from '@/hooks/use-session';
import { ConfirmDialog } from '../components/confirm-dialog';
import { SettingsSection } from '../components/settings-section';

const DetailsSection = ({ canEdit }: { canEdit: boolean }) => {
  const queryClient = useQueryClient();
  const { error, captureError, clearError } = useFormError();
  const [saved, setSaved] = React.useState(false);

  const organizationQuery = useQuery({
    queryKey: ['organization'],
    queryFn: organizationApi.getOrganization,
  });

  const mutation = useMutation({
    mutationFn: organizationApi.updateOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      // The switcher and greeting read the name from the user payload.
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: captureError,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();
    setSaved(false);

    const form = new FormData(event.currentTarget);
    mutation.mutate({
      name: String(form.get('name')).trim(),
      timezone: String(form.get('timezone')).trim() || 'UTC',
    });
  };

  if (organizationQuery.isLoading) {
    return <Skeleton className="h-64 w-full rounded-card" />;
  }

  const organization = organizationQuery.data;

  return (
    <SettingsSection
      title="Organization"
      description="Used across the product and on anything you share."
      onSubmit={canEdit ? handleSubmit : undefined}
      footer={
        canEdit ? (
          <>
            {saved ? (
              <span className="mr-auto text-sm text-positive">Saved</span>
            ) : null}
            <Button type="submit" loading={mutation.isPending}>
              Save changes
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {!canEdit ? (
          <Alert tone="info">
            Only admins and owners can change organization settings.
          </Alert>
        ) : null}

        <Field label="Name" htmlFor="name">
          <Input
            name="name"
            defaultValue={organization?.name ?? ''}
            disabled={!canEdit}
            required
          />
        </Field>

        <Field
          label="Reporting timezone"
          htmlFor="timezone"
          hint="An IANA zone such as Europe/Zurich. Day boundaries for metrics follow it."
        >
          <Input
            name="timezone"
            defaultValue={organization?.timezone ?? 'UTC'}
            disabled={!canEdit}
            placeholder="UTC"
          />
        </Field>

        <Field label="Identifier" htmlFor="slug">
          <Input
            name="slug"
            value={organization?.slug ?? ''}
            readOnly
            disabled
            className="bg-surface-muted font-mono text-xs"
          />
        </Field>
      </div>
    </SettingsSection>
  );
};

const CreateOrganizationSection = () => {
  const queryClient = useQueryClient();
  const { switchOrganization } = useSession();
  const { error, captureError, clearError } = useFormError();
  const formRef = React.useRef<HTMLFormElement>(null);

  const mutation = useMutation({
    mutationFn: organizationApi.createOrganization,
    onSuccess: (organization) => {
      formRef.current?.reset();
      // Land the user in what they just created rather than leaving them behind.
      switchOrganization(organization.id);
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
    },
    onError: captureError,
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearError();

    const name = String(new FormData(event.currentTarget).get('name')).trim();

    if (name) {
      mutation.mutate({ name });
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <SettingsSection
        title="New organization"
        description="A separate workspace with its own projects, members and billing sources."
        footer={
          <Button
            type="submit"
            variant="secondary"
            loading={mutation.isPending}
          >
            Create organization
          </Button>
        }
      >
        <div className="space-y-4">
          {error ? <Alert tone="error">{error}</Alert> : null}

          <Field label="Name" htmlFor="new-organization-name">
            <Input name="name" placeholder="Acme Labs" required />
          </Field>
        </div>
      </SettingsSection>
    </form>
  );
};

const DeleteOrganizationSection = ({ name }: { name: string }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { switchOrganization } = useSession();
  const { error, captureError, clearError } = useFormError();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const mutation = useMutation({
    mutationFn: organizationApi.deleteOrganization,
    onSuccess: () => {
      // The current organization is gone; drop the override so the server picks
      // the next default on the following request.
      switchOrganization('');
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
      navigate('/', { replace: true });
    },
    onError: (err) => {
      setDialogOpen(false);
      captureError(err);
    },
  });

  return (
    <>
      <SettingsSection
        title="Delete organization"
        description="Removes this organization, its projects and all connected sources. This cannot be undone."
        footer={
          <Button
            variant="danger"
            onClick={() => {
              clearError();
              setDialogOpen(true);
            }}
          >
            Delete organization
          </Button>
        }
      >
        {error ? <Alert tone="error">{error}</Alert> : null}
      </SettingsSection>

      <ConfirmDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={`Delete ${name}?`}
        description="Every project, API key and finding in this organization becomes inaccessible. Members lose access immediately."
        confirmLabel="Delete organization"
        confirmationPhrase={name}
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate()}
      />
    </>
  );
};

export const OrganizationTab = () => {
  const { user } = useSession();
  const role = user?.currentOrganization.role;
  const canEdit = role === 'owner' || role === 'admin';

  const current = user?.organizations.find(
    (organization) => organization.id === user.currentOrganization.id,
  );

  return (
    <div className="space-y-6">
      <DetailsSection canEdit={canEdit} />
      <CreateOrganizationSection />
      {role === 'owner' && current ? (
        <DeleteOrganizationSection name={current.name} />
      ) : null}
    </div>
  );
};
